import { apiPaths } from '@core/http/apiPaths';
import { httpClient } from '@core/http/httpClient';
import { EntityReducers } from '@core/store/EntityReducers';
import { getPathPayload, omitAbortSignalConfig } from '@hwm/ui-lib';
import { Clusters } from '@models/Clusters';
import { ISite, ISiteDetails, ISiteDetailsResponse, ISitesForMapResponse } from '@models/ISite';
import { ISitePhoto } from '@models/ISitePhoto';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { sitesAndLnsFilters } from '@shared/components/SiteSharedFilters/constants';
import { ValueOf } from '@shared/utils/valueOf';
import { AxiosResponse } from 'axios';
import { isNil, isNumber } from 'lodash';
import { getParamsForGetSitesForMap } from './sitesUtils';

export interface BaseSiteParams {
  accountId: number;
  filter?: Partial<Omit<{ [key in ValueOf<typeof sitesAndLnsFilters>]: string }, 'tab' | 'update'>>;
  namedListId?: number;
}

export interface Bounds {
  pointOneLatitude: number;
  pointOneLongitude: number;
  pointTwoLatitude: number;
  pointTwoLongitude: number;
}

export interface GetSitesForMapParams extends BaseSiteParams, Partial<Bounds> {
  directlyAssociated?: boolean;
  showPressureSites?: boolean;
  showFlowSites?: boolean;
  showFlowAndPressureSites?: boolean;
  showPrvSites?: boolean;
  showSitesWithInvalidLocation?: boolean;
  includeArchived: boolean;
}

const getSitesForMap = createAsyncThunk(
  `${EntityReducers.sites}/getSitesForMap`,
  async (payload: GetSitesForMapParams) => {
    const params = getParamsForGetSitesForMap(payload);

    const response: AxiosResponse<ISitesForMapResponse> = await httpClient.get(apiPaths.sites.getAll, {
      params,
    });

    return response.data;
  }
);

interface IGetClustersParams extends BaseSiteParams, Partial<Bounds> {
  zoom: number;
  showPressureSites: boolean;
  showFlowSites: boolean;
  showFlowAndPressureSites: boolean;
  showPrvSites: boolean;
  showSitesWithInvalidLocation?: boolean;
  includeArchived: boolean;
}

const getClusters = createAsyncThunk(`${EntityReducers.sites}/getClusters`, async (payload: IGetClustersParams) => {
  const { from, to, ...filters } = payload.filter;

  const params = new URLSearchParams(filters as Record<string, string>);

  from && params.append('calledInFilter.From', from);
  to && params.append('calledInFilter.To', to);

  payload.accountId && params.append('accountId', payload.accountId.toString());
  params.append('zoom', payload.zoom.toString());
  isNumber(payload.pointOneLatitude) && params.append('pointOneLatitude', payload.pointOneLatitude.toString());
  isNumber(payload.pointOneLongitude) && params.append('pointOneLongitude', payload.pointOneLongitude.toString());
  isNumber(payload.pointTwoLatitude) && params.append('pointTwoLatitude', payload.pointTwoLatitude.toString());
  isNumber(payload.pointTwoLongitude) && params.append('pointTwoLongitude', payload.pointTwoLongitude.toString());
  !isNil(payload.showSitesWithInvalidLocation) &&
    params.append('showSitesWithInvalidLocation', payload.showSitesWithInvalidLocation?.toString());

  params.append('showPressureSites', payload.showPressureSites?.toString());
  params.append('showFlowSites', payload.showFlowSites?.toString());
  params.append('showFlowAndPressureSites', payload.showFlowAndPressureSites?.toString());
  params.append('showPrvSites', payload.showPrvSites?.toString());
  params.append('includeArchived', payload.includeArchived?.toString());

  const response: AxiosResponse<Clusters> = await httpClient.get(apiPaths.sites.clusters, {
    params,
  });

  return response.data;
});

export const SITES_PAGE_SIZE = 100;

interface GetSitesListParams extends BaseSiteParams {
  pageSize?: number;
  currentPage?: number;
  orderBy?: string;
  ascending?: boolean;
  includeArchived: boolean;
}

const getSitesList = createAsyncThunk(`${EntityReducers.sites}/getSitesList`, async (payload: GetSitesListParams) => {
  const { from, to, ...filters } = payload.filter;

  const params = new URLSearchParams(filters as Record<string, string>);

  params.append('pageSize', SITES_PAGE_SIZE.toString());
  params.append('currentPage', payload?.currentPage.toString());

  payload?.orderBy && params.append('orderBy', payload?.orderBy);
  payload?.ascending && params.append('ascending', payload.ascending.toString());

  payload.accountId && params.append('accountId', payload.accountId.toString());

  payload.namedListId && params.append('namedListId', payload.namedListId.toString());

  from && params.append('calledInFilter.From', from);
  to && params.append('calledInFilter.To', to);

  params.append('includeArchived', payload.includeArchived?.toString());

  const response: AxiosResponse<ISiteDetailsResponse> = await httpClient.get(apiPaths.sites.getSiteList, { params });

  return {
    ...response.data,
    items: response.data.items.map((site) => ({
      ...site,
      lastCallIn: site.lastCallIn,
      lastDataReceived: site.lastDataReceived,
      status: site.isLeaking ? 'Leak' : 'No leak',
    })),
  };
});

interface GetSiteDetailsParams extends getPathPayload<typeof apiPaths.sites.details> {
  disableCanceling?: boolean;
}

const getSiteDetails = createAsyncThunk(`${EntityReducers.sites}/getDetails`, async (payload: GetSiteDetailsParams) => {
  const { disableCanceling, ...data } = payload;

  const response: AxiosResponse<ISite> = await httpClient.get(
    `${apiPaths.sites.details(data)}`,
    disableCanceling ? { signal: omitAbortSignalConfig } : {}
  );

  return {
    ...response.data,
    status: response.data.isLeaking ? 'Leak' : 'No leak',
  };
});

const saveSiteDetails = createAsyncThunk(
  `${EntityReducers.sites}/saveDetails`,
  async ({ pathParams, body }: { pathParams: getPathPayload<typeof apiPaths.sites.details>; body: ISiteDetails }) => {
    const response: AxiosResponse<ISite> = await httpClient.put(`${apiPaths.sites.details(pathParams)}`, body);

    return {
      ...body,
      ...response.data,
      id: Number(pathParams.siteId),
    };
  }
);

const saveSiteLocation = createAsyncThunk(
  `${EntityReducers.sites}/saveLocation`,
  async ({ pathParams, body }: { pathParams: getPathPayload<typeof apiPaths.sites.saveLocation>; body: ISite }) => {
    await httpClient.put(`${apiPaths.sites.saveLocation(pathParams)}`, body);

    return body;
  }
);

const uploadPhotos = createAsyncThunk(
  `${EntityReducers.sites}/uploadPhotos`,
  async ({ pathParams, body }: { pathParams: getPathPayload<typeof apiPaths.sites.photo>; body: File[] }) => {
    const promises = body.map(async (file) => {
      const bodyFormData = new FormData();
      bodyFormData.append('file', file);
      bodyFormData.append('description', file.name);
      return httpClient.post<ISitePhoto>(`${apiPaths.sites.photo(pathParams)}`, bodyFormData);
    });

    await Promise.all(promises);
  }
);

// FAVORITE ENDPOINTS
const loadFavoriteNamedList = createAsyncThunk(
  `${EntityReducers.sites}/loadNamedList`,
  async ({ listId }: { listId?: number }) => {
    const url = apiPaths.accounts.favoriteNamedList;

    const response = await httpClient.post(url, listId ? { listId } : {});

    return response.data;
  }
);

const getSelectedSitesIds = createAsyncThunk(
  `${EntityReducers.sites}/getSelectedSitesIds`,
  async ({ accountId }: { accountId?: string }) => {
    const url = apiPaths.sites.favoriteAll;

    const response: AxiosResponse<number[]> = await httpClient.get(
      url,
      accountId
        ? {
            params: { accountId },
          }
        : null
    );

    return response.data;
  }
);

const addSiteAsSelected = createAsyncThunk(
  `${EntityReducers.sites}/addSiteAsSelected`,
  async ({ siteId }: getPathPayload<typeof apiPaths.sites.favorite>) => {
    const url = apiPaths.sites.favorite({ siteId });

    const response = await httpClient.post(url);

    return { ...response.data, siteId };
  }
);

const removeSiteAsSelected = createAsyncThunk(
  `${EntityReducers.sites}/removeSelectedSite`,
  async ({ siteId }: getPathPayload<typeof apiPaths.sites.favorite>) => {
    const url = apiPaths.sites.favorite({ siteId });

    const response = await httpClient.delete(url);

    return { ...response.data, siteId };
  }
);

export interface SitesAllSelectedParams extends BaseSiteParams {
  smsNumbers?: string[];
  siteIds?: number[];
  isLnsNewLeaksPage?: boolean;
  includeArchived?: boolean;
}

const addAllSitesAsSelected = createAsyncThunk(
  `${EntityReducers.sites}/addAllSitesAsSelected`,
  async ({ accountId, filter, smsNumbers, siteIds, isLnsNewLeaksPage, includeArchived }: SitesAllSelectedParams) => {
    const url = apiPaths.sites.favoriteAll;

    const isSMSAdding = !isNil(smsNumbers);
    const isOnePage = !isSMSAdding && !isNil(siteIds);
    const isFiltered = !isSMSAdding && !isOnePage && Boolean(filter);

    const { from, to, filterBySelected, filterByMonitored, ...filters } = filter ?? {};

    const response = await httpClient.post(url, {
      accountId,
      smsNumbers: isSMSAdding ? smsNumbers : null,
      siteIds: isOnePage ? siteIds : null,
      ...(isFiltered
        ? {
            ...filters,
            includeArchived,
            filterBySelected: filterBySelected === 'true',
            filterByMonitored: filterByMonitored === 'true',
            ...(!isLnsNewLeaksPage && from && to ? { calledInFilter: { from, to } } : {}),
          }
        : {}),
    });

    return { ...response.data };
  }
);

const removeAllSitesAsSelected = createAsyncThunk(
  `${EntityReducers.sites}/removeAllSitesAsSelected`,
  async ({ accountId, filter, siteIds, isLnsNewLeaksPage, includeArchived }: SitesAllSelectedParams) => {
    const url = apiPaths.sites.favoriteAll;

    const isOnePage = !isNil(siteIds);
    const isFiltered = !isOnePage && Boolean(filter);

    const { from, to, filterBySelected, filterByMonitored, ...filters } = filter ?? {};

    const response: AxiosResponse = await httpClient.delete(url, {
      data: {
        accountId,
        siteIds: isOnePage ? siteIds : null,
        ...(isFiltered
          ? {
              ...filters,
              includeArchived,
              filterBySelected: filterBySelected === 'true',
              filterByMonitored: filterByMonitored === 'true',
              ...(!isLnsNewLeaksPage && from && to ? { calledInFilter: { from, to } } : {}),
            }
          : {}),
      },
    });

    return { ...response.data };
  }
);

export const thunks = {
  getSitesForMap,
  getClusters,
  getSitesList,
  getSiteDetails,
  saveSiteDetails,
  saveSiteLocation,
  uploadPhotos,
  // favorite:
  loadFavoriteNamedList,
  getSelectedSitesIds,
  addSiteAsSelected,
  removeSiteAsSelected,
  addAllSitesAsSelected,
  removeAllSitesAsSelected,
};
